import { Box, CircularProgress, Stack } from '@mui/material';
import * as webeid from '@web-eid/web-eid-library/web-eid';
import * as Config from 'Config';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdcardSocketMessage } from '../../../../common/types/idcard-socket-message';
import { IdCardSessionResultCode, IdCardSocketResponse } from '../../../../common/types/idcard-socket-response';
import { InlineNotification, Button } from '../../../../components';
import ClientDataContext from '../../../../store/client-data-context';
import { BackToServiceProviderLink } from '../';
import { AuthErrorResponseCode } from '../../../../common/types/auth-error-response';

interface IdCardSubFormProps {
  socketErrorCode: string;
  setSocketErrorCode: (value: string) => void;
}

export const IdCardSubForm = ({ socketErrorCode, setSocketErrorCode }: IdCardSubFormProps) => {
  const { t, i18n } = useTranslation();

  const clientCtx = useContext(ClientDataContext);

  const [isAuthorizationWaiting, setIsAuthorizationWaiting] = useState(false);

  const submitHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    getNewSocketConnection();
  };

  const getNewSocketConnection = () => {
    setSocketErrorCode('');

    const ws = new WebSocket(Config.IDCARD_AUTH_SOCKET_URL);
    setIsAuthorizationWaiting(true);

    ws.onopen = () => {
      // https://github.com/micronaut-projects/micronaut-core/issues/11252
      setTimeout(() => {
        ws.send(
            JSON.stringify({
              _csrf: clientCtx.client._csrf,
            } as IdcardSocketMessage)
        );
      }, 50);
    };

    ws.onmessage = (e) => {
      const response: IdCardSocketResponse = JSON.parse(e.data);
      if (response.nonce) {
        const lang = i18n.language.split('-')[0];
        webeid
          .authenticate(response.nonce, { lang })
          .then((authToken) => {
            ws.send(
              JSON.stringify({
                _csrf: clientCtx.client._csrf,
                authToken: authToken,
              } as IdcardSocketMessage)
            );
          })
          .catch((err) => {
            const error = err.code;
            if (ws.readyState === WebSocket.CLOSED) {
              return;
            }
            if (error === webeid.ErrorCode.ERR_WEBEID_USER_CANCELLED) {
              ws.close(1000, 'user_cancel');
              setSocketErrorCode(error);
            } else if (
              error === webeid.ErrorCode.ERR_WEBEID_USER_TIMEOUT ||
              error === webeid.ErrorCode.ERR_WEBEID_ACTION_TIMEOUT
            ) {
              ws.close(1000, 'timeout');
              setSocketErrorCode(error);
            } else {
              ws.close(1000, 'internal_error');
              setSocketErrorCode(IdCardSessionResultCode.INTERNAL_ERROR);
            }
          });
      } else if (response.finished && response.result != undefined) {
        if (response.result == IdCardSessionResultCode.OK) {
          ws.close(1000, 'ID-card result OK, redirecting');
          window.location.href = Config.AUTHORIZE_URL + '?' + clientCtx.client.authorizeRequestParams;
        } else {
          setSocketErrorCode(response.result);
        }
      }
    };

    ws.onerror = () => {
      setSocketErrorCode(IdCardSessionResultCode.INTERNAL_ERROR);
    };

    ws.onclose = (e) => {
      if (e.code == 1001) {
        setSocketErrorCode(webeid.ErrorCode.ERR_WEBEID_USER_TIMEOUT);
      }

      setIsAuthorizationWaiting(false);
    };

    return ws;
  };

  useEffect(() => {
    setSocketErrorCode('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const idCardForm = (
    <Stack sx={{
      gap: 4
    }}>
      {socketErrorCode && socketErrorCode != 'INTERNAL_ERROR' && (
        <InlineNotification appearance="error">
          <div {...{ 'data-testid': 'formError' }}>{t(`form.socketError.${socketErrorCode.toUpperCase()}.text`)}</div>
        </InlineNotification>
      )}

      <Button appearance="primary" size="m" onClick={submitHandler} {...{ 'data-testid': 'continueBtn' }}>
        {t('form.general.continueBtn')}
      </Button>
    </Stack>
  );

  return (
    <>
      {isAuthorizationWaiting ? (
        <Stack
          direction="row"
          sx={{
            mb: 2,
            justifyContent: 'center'
          }}>
          <CircularProgress color="primary" />
        </Stack>
      ) : (
        idCardForm
      )}
      <Box sx={{
        my: 2
      }}>
        <BackToServiceProviderLink errorCode={AuthErrorResponseCode.USER_CANCEL} />
      </Box>
    </>
  );
};
